import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, onSessionExpired } from '../lib/api';

export type UserRole = 'ADMIN' | 'EDITOR' | 'CLIENT';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  customerId: string | null;
  phone?: string | null;
  passwordChangeRequired: boolean;
  customer?: { businessName: string; tradeName: string | null } | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  isInternal: boolean;
  /** Sólo ADMIN gestiona usuarios, integraciones y reglas de negocio. */
  isAdmin: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INTERNAL_ROLES: UserRole[] = ['ADMIN', 'EDITOR'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<AuthUser>('/auth/me', { silent401: true });
      setUser(me);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Cualquier 401 en la aplicación devuelve al usuario al inicio de sesión,
  // sin que cada pantalla tenga que preocuparse por ello.
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      setUser(null);
      setStatus('anonymous');
    });
    return () => { unsubscribe(); };
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    const response = await api.post<{ user: AuthUser }>(
      '/auth/login',
      { email, password, rememberMe },
      { silent401: true },
    );
    setUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Aunque el servidor falle, localmente se cierra la sesión.
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await api.post('/auth/password', { currentPassword, newPassword });
    await refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    status,
    isInternal: user ? INTERNAL_ROLES.includes(user.role) : false,
    isAdmin: user?.role === 'ADMIN',
    login,
    logout,
    changePassword,
    refresh,
  }), [user, status, login, logout, changePassword, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}
